import { Alert, Badge, Button, buttonClass, cardClass, fieldClass, SearchField, selectClass, textareaClass } from '@ledgerloom/ui';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, propose } from '../api';
import { useApi, useDebounced, usePageTitle } from '../hooks';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { Empty, ErrorBanner, Expandable, Icon, ListSkeleton, Time, outcomeMessage, outcomeTone, useToast, badgeTone } from '../components/ui';
import { PartyLink } from '../components/PartyName';
import { isAdmin, useSession } from '../session';
import { mailText, snippetText, type Span } from '../mailText';

// ADR 0021: your mailbox and the team mailbox, laid out like Gmail. A rail of
// mailboxes, each with its folders; the chosen folder's conversations, each
// with a checkbox for actions on several at once; and the open conversation in
// place of the list, older messages folded, HTML mail in a sandboxed frame.
// A conversation has a status and may be assigned to a person; owners and
// admins may delete one, which moves it to Trash and erases nothing.
// Mailbox, folder, filter, search, and conversation are held in the URL.
// Patterns: navigation/sidebar and data-display/tree-view for the rail,
// data-display/list-view with forms/checkbox for the list, forms/search-field,
// content-management/accordion for the messages, forms/text-field for the reply.
// Message text is data: shown as text (mailText.ts), and HTML only inside a
// frame that runs no script and loads nothing remote until asked.

interface Mailbox { id: string; kind: 'user' | 'team'; name: string; address: string | null; mine: boolean; access: string; open_threads: number; unread: number; connected: Array<{ provider: string; email: string }> }
type Status = 'open' | 'working' | 'on_hold' | 'closed';
interface ThreadRow { id: string; subject: string; status: Status; party_id: string | null; party_name: string | null; last_message_at: string; messages: number; last_direction: string | null; last_from: string | null; assignee_user_id: string | null; assignee_name: string | null; snippet: string; deleted_at: string | null; unread: boolean; attachments: number }
interface Attachment { id: string; filename: string; content_type: string; bytes: number; inline: boolean; content_id: string | null; kept: boolean }
interface Message { id: string; direction: 'in' | 'out'; from_address: string; to_addresses: string[]; subject: string; body_text: string; body_html: string | null; at: string; automated: Automated | null; attachments: Attachment[] }
interface Upload { id: string; filename: string; content_type: string; bytes: number }
interface Thread { id: string; subject: string; status: Status; party_id: string | null; party_name: string | null; mailbox_id: string | null; assignee_user_id: string | null; assignee_name: string | null; deleted_at: string | null; messages: Message[] }
interface Person { id: string; display_name: string; status: string }
// Whose images from the web you always see (GET /v1/mail/images).
interface ImageChoices { everyone: boolean; senders: string[] }

type Automated = 'no_reply_address' | 'auto_submitted' | 'bulk' | 'mailing_list';
const AUTOMATED: Record<Automated, string> = { no_reply_address: 'No-reply address', auto_submitted: 'Automatic reply', bulk: 'Bulk mail', mailing_list: 'List mail' };

// The server's statuses (engines/messaging/thread-status.ts), as people read them.
const STATUS: Record<Status, { label: string; tone: string }> = {
  open: { label: 'Open', tone: 'info' }, working: { label: 'Working', tone: 'ok' }, on_hold: { label: 'On hold', tone: 'warn' }, closed: { label: 'Closed', tone: '' },
};
const STATUSES = Object.keys(STATUS) as Status[];

// A mailbox's folders: what each lists, its count, and who sees it.
type FolderKey = 'inbox' | 'closed' | 'trash';
const FOLDERS: Record<FolderKey, { label: string; query: Record<string, string>; count?: (m: Mailbox) => number; unit?: string; admin?: true }> = {
  inbox: { label: 'Inbox', query: { status: 'active' }, count: (m) => m.unread, unit: 'unread' },
  closed: { label: 'Closed', query: { status: 'closed' } },
  trash: { label: 'Trash', query: { deleted: 'true' }, admin: true },
};
const FOLDER_KEYS = Object.keys(FOLDERS) as FolderKey[];
const ASSIGNED = 'assigned';

const boxName = (m: Mailbox) => (m.mine ? 'Your mailbox' : `${m.name} mailbox`);
const size = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
const fileUrl = (messageId: string, a: Attachment, inline = false) => `/v1/messages/${messageId}/attachments/${a.id}${inline ? '?inline=true' : ''}`;
// Gmail's dates: the time for today, the day for this year, the full date before.
const mailWhen = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
};

export function Mail() {
  usePageTitle('Mail');
  const { me } = useSession();
  const admin = isAdmin(me);
  const [params, setParams] = useSearchParams();
  const [version, setVersion] = useState(0);
  const boxes = useApi<Mailbox[]>('/v1/mailboxes', [version]);
  const assignedCount = useApi<ThreadRow[]>('/v1/mail/assigned?status=active', [version]);
  const people = useApi<Person[]>('/v1/users');
  const assigned = params.get('box') === ASSIGNED;
  const box = assigned ? null : boxes.data?.find((m) => m.id === params.get('box')) ?? boxes.data?.[0] ?? null;
  const asked = params.get('folder') as FolderKey | null;
  const folder: FolderKey = asked && FOLDER_KEYS.includes(asked) && (admin || !FOLDERS[asked].admin) ? asked : 'inbox';
  const filter = STATUSES.includes(params.get('status') as Status) ? params.get('status') as Status : null;
  const q = params.get('q') ?? '';
  const thread = params.get('thread');
  const compose = params.get('compose') === '1';
  const go = (patch: Record<string, string | null>, replace = false) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) if (v === null) next.delete(k); else next.set(k, v);
    setParams(next, { replace });
  };
  const at = (b: string, f: FolderKey = 'inbox') => `?${new URLSearchParams({ box: b, ...(f !== 'inbox' ? { folder: f } : {}) })}`;
  const label = assigned ? 'Assigned to you' : box ? (folder === 'inbox' ? boxName(box) : `${boxName(box)}: ${FOLDERS[folder].label}`) : 'Mail';
  const changed = () => setVersion((v) => v + 1);

  // The list's query: the folder's, narrowed by a status and by the search.
  const query = new URLSearchParams(assigned ? { status: 'active' } : FOLDERS[folder].query);
  if (filter && query.get('status') === 'active') query.set('status', filter);
  if (q) query.set('q', q);
  const listPath = assigned ? `/v1/mail/assigned?${query}` : box ? `/v1/mailboxes/${box.id}/threads?${query}` : null;
  const filterable = assigned || folder === 'inbox';

  return (
    <div className="stack">
      <div className="page-head mail-head">
        <h1>Mail</h1>
        <SearchBox value={q} onChange={(v) => go({ q: v || null, thread: null }, true)} />
      </div>
      <ErrorBanner error={boxes.error} />
      <div className={`inbox mail${thread || compose ? ' has-item' : ''}`}>
        <nav className="inbox-subjects" aria-label="Mailboxes">
          <Link className={buttonClass({ className: 'mail-compose' })} to={`?${new URLSearchParams({ ...Object.fromEntries(params), compose: '1' })}`}><Icon name="plus" />Compose</Link>
          <ul className="tree">
            <li><FolderLink to={at(ASSIGNED)} label="Assigned to you" count={assignedCount.data?.length ?? 0} unit="assigned" current={assigned} /></li>
            {(boxes.data ?? []).map((m) => (
              <li key={m.id}>
                <FolderLink to={at(m.id)} label={boxName(m)} count={m.open_threads} unit="active" current={box?.id === m.id && folder === 'inbox'} />
                <ul>
                  {FOLDER_KEYS.filter((f) => f !== 'inbox' && (admin || !FOLDERS[f].admin)).map((f) => (
                    <li key={f}><FolderLink to={at(m.id, f)} label={FOLDERS[f].label} count={FOLDERS[f].count?.(m) ?? 0} unit={FOLDERS[f].unit ?? ''} current={box?.id === m.id && folder === f} /></li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <p className="small mail-settings"><Link to="/admin#mail-h">Mail settings</Link></p>
        </nav>
        <section className="mail-pane" aria-label={compose ? 'New message' : thread ? 'Open conversation' : label}>
          {compose && boxes.data ? (
            <Compose boxes={boxes.data} from={box} onClose={() => go({ compose: null })} onSent={(boxId) => { changed(); go({ compose: null, box: boxId, folder: null, thread: null }); }} />
          ) : thread && (box || assigned) ? (
            <ThreadView key={thread} id={thread} box={box} back={label} onBack={() => go({ thread: null })} people={people.data ?? []} admin={admin} me={me.user_id}
              onChange={changed} onGone={() => { changed(); go({ thread: null }); }} />
          ) : (
            <>
              <div className="inbox-pane-head">
                <h2 id="mail-list-h" tabIndex={-1}>{label}</h2>
                <Link className={buttonClass({ size: 'sm', className: 'mail-compose-narrow' })} to={`?${new URLSearchParams({ ...Object.fromEntries(params), compose: '1' })}`}><Icon name="plus" />Compose</Link>
                <div className="inbox-subject-select">
                  <label className="sr-only" htmlFor="mail-folder">Mailbox</label>
                  <select id="mail-folder" className={selectClass} value={assigned ? ASSIGNED : box ? `${box.id}/${folder}` : ''}
                    onChange={(e) => { const [b, f] = e.target.value.split('/'); go({ box: b!, folder: f && f !== 'inbox' ? f : null, status: null, thread: null }, true); }}>
                    <option value={ASSIGNED}>Assigned to you ({assignedCount.data?.length ?? 0})</option>
                    {(boxes.data ?? []).flatMap((m) => FOLDER_KEYS.filter((f) => admin || !FOLDERS[f].admin).map((f) => (
                      <option key={`${m.id}/${f}`} value={`${m.id}/${f}`}>{f === 'inbox' ? boxName(m) : `  ${FOLDERS[f].label}`}{FOLDERS[f].count ? ` (${FOLDERS[f].count!(m)})` : ''}</option>
                    )))}
                  </select>
                </div>
              </div>
              {box?.address && folder === 'inbox' && !q && <p className="small muted mail-address">Mail to <span className="mono">{box.address}</span> lands here.{box.mine && box.connected.length ? ` So does ${box.connected.map((c) => c.email).join(', ')}.` : ''}</p>}
              {listPath ? (
                <ThreadList key={`${assigned ? ASSIGNED : box?.id}/${folder}/${filter}`} path={listPath} label={label} version={version} people={people.data ?? []} me={me.user_id}
                  admin={admin} trash={!assigned && folder === 'trash'} searching={!!q} link={(t) => `?${new URLSearchParams({ ...Object.fromEntries(params), thread: t })}`}
                  onChange={changed} empty={assigned ? 'Nothing is assigned to you.' : box?.address ? `Send a message to ${box.address} to see it here.` : 'Mail sent and received here shows up as conversations.'}
                  filter={filterable ? <StatusFilter value={filter} onChange={(s) => go({ status: s, thread: null }, true)} /> : null} />
              ) : boxes.error ? null : <ListSkeleton rows={4} label="Loading conversations" />}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [text, setText] = useState(value);
  const typed = useDebounced(text, 300);
  useEffect(() => { if (typed.trim() !== value) onChange(typed.trim()); }, [typed]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <form role="search" className="mail-search" onSubmit={(e) => { e.preventDefault(); onChange(text.trim()); }}>
      <SearchField id="mail-q" label="Search mail" placeholder="Search mail" value={text} onValueChange={setText} className="rounded-pill bg-surface-muted" />
    </form>
  );
}

function StatusFilter({ value, onChange }: { value: Status | null; onChange: (s: Status | null) => void }) {
  return (
    <>
      <label className="sr-only" htmlFor="mail-status">Show</label>
      <select id="mail-status" className={`${selectClass} h-9 w-auto`} value={value ?? ''} onChange={(e) => onChange((e.target.value || null) as Status | null)}>
        <option value="">Every status</option>
        {STATUSES.filter((s) => s !== 'closed').map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
      </select>
    </>
  );
}

// A menu of actions behind a button (details/summary): Escape or a pick closes it.
function Menu({ label, children }: { label: ReactNode; children: (close: () => void) => ReactNode }) {
  const ref = useRef<HTMLDetailsElement>(null);
  const close = () => { if (ref.current) { ref.current.open = false; ref.current.querySelector('summary')?.focus(); } };
  useEffect(() => {
    const away = (e: MouseEvent) => { if (ref.current?.open && !ref.current.contains(e.target as Node)) ref.current.open = false; };
    document.addEventListener('click', away);
    return () => document.removeEventListener('click', away);
  }, []);
  return (
    <details className="menu" ref={ref} onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } }}>
      <summary className={buttonClass({ variant: 'secondary', size: 'sm', className: 'list-none' })}>{label}</summary>
      <div className="menu-pop">{children(close)}</div>
    </details>
  );
}

function StatusMenu({ current, onPick }: { current?: Status; onPick: (s: Status) => void }) {
  return (
    <Menu label={current ? <>Status: {STATUS[current].label}</> : 'Status'}>
      {(close) => STATUSES.map((s) => (
        <button key={s} type="button" className="menu-item" aria-current={s === current ? 'true' : undefined} onClick={() => { close(); onPick(s); }}>{STATUS[s].label}</button>
      ))}
    </Menu>
  );
}

function AssignMenu({ current, people, me, onPick }: { current?: { id: string | null; name: string | null }; people: Person[]; me: string; onPick: (id: string | null, name: string) => void }) {
  const active = people.filter((p) => p.status === 'active' && p.id !== me);
  const self = people.find((p) => p.id === me);
  return (
    <Menu label={current?.id ? <>Assigned to {current.id === me ? 'you' : current.name}</> : 'Assign'}>
      {(close) => (
        <>
          {self && current?.id !== me && <button type="button" className="menu-item" onClick={() => { close(); onPick(me, 'you'); }}>Assign to me</button>}
          {active.map((p) => <button key={p.id} type="button" className="menu-item" aria-current={p.id === current?.id ? 'true' : undefined} onClick={() => { close(); onPick(p.id, p.display_name); }}>{p.display_name}</button>)}
          {current?.id !== null && <button type="button" className="menu-item" onClick={() => { close(); onPick(null, 'nobody'); }}>Unassign</button>}
        </>
      )}
    </Menu>
  );
}

// Keyed by folder and filter, so another folder never shows this one's rows or
// selection while it loads. `version` reloads it after a change elsewhere.
function ThreadList({ path, label, version, people, me, admin, trash, searching, link, onChange, empty, filter }: {
  path: string; label: string; version: number; people: Person[]; me: string; admin: boolean; trash: boolean; searching: boolean;
  link: (thread: string) => string; onChange: () => void; empty: string; filter: ReactNode;
}) {
  const t = useApi<ThreadRow[]>(path, [version]);
  const toast = useToast();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const all = useRef<HTMLInputElement>(null);
  const rows = t.data ?? [];
  const ids = rows.filter((r) => picked.has(r.id)).map((r) => r.id);
  useEffect(() => { if (all.current) all.current.indeterminate = ids.length > 0 && ids.length < rows.length; }, [ids.length, rows.length]);
  const act = async (route: string, body: Record<string, unknown>, done: string) => {
    setBusy(true); setError(null);
    try {
      await api(route, { body: { thread_ids: ids, ...body } });
      toast(done); setPicked(new Set()); await t.reload(); onChange();
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const n = (k: number) => `${k} ${k === 1 ? 'conversation' : 'conversations'}`;

  return (
    <>
      <div className="mail-toolbar" role="group" aria-label="Conversation actions">
        <input ref={all} type="checkbox" className="mail-check" aria-label="Select every conversation shown" disabled={!rows.length}
          checked={rows.length > 0 && ids.length === rows.length} onChange={(e) => setPicked(new Set(e.target.checked ? rows.map((r) => r.id) : []))} />
        {ids.length ? (
          <>
            <span className="small" role="status">{ids.length} selected</span>
            {trash ? (
              <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void act('/v1/threads/restore', {}, `Restored ${n(ids.length)}`)}>Restore</Button>
            ) : (
              <>
                <StatusMenu onPick={(s) => void act('/v1/threads/triage', { status: s }, `Marked ${n(ids.length)} ${STATUS[s].label.toLowerCase()}`)} />
                <AssignMenu people={people} me={me} onPick={(id, name) => void act('/v1/threads/triage', { assignee_user_id: id }, id ? `Assigned ${n(ids.length)} to ${name}` : `Unassigned ${n(ids.length)}`)} />
                {admin && <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void act('/v1/threads/delete', {}, `Moved ${n(ids.length)} to Trash`)}>Delete</Button>}
              </>
            )}
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void act('/v1/threads/read', { read: true }, `Marked ${n(ids.length)} read`)}>Mark read</Button>
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void act('/v1/threads/read', { read: false }, `Marked ${n(ids.length)} unread`)}>Mark unread</Button>
          </>
        ) : (
          <Button type="button" variant="ghost" size="sm" disabled={t.loading} onClick={() => void t.reload()}>Refresh</Button>
        )}
        <span className="grow" />
        {filter}
      </div>
      <ErrorBanner error={error ?? t.error} />
      {trash && <p className="small muted mail-address">Deleted conversations keep every message. Only owners and admins see them here; Restore puts them back.</p>}
      {!t.data ? (t.error ? null : <ListSkeleton rows={4} label="Loading conversations" />) : !rows.length ? (
        <Empty title={searching ? 'Nothing matches' : trash ? 'Trash is empty' : 'No conversations here'}>{searching ? 'Try other words, or another folder.' : trash ? null : empty}</Empty>
      ) : (
        <ul className="mail-rows" aria-label={`Conversations in ${label}`}>
          {rows.map((r) => (
            <li key={r.id} className={`mail-row${r.unread ? ' unread' : ''}${picked.has(r.id) ? ' picked' : ''}`}>
              <input type="checkbox" className="mail-check" aria-label={`Select ${r.subject}`} checked={picked.has(r.id)}
                onChange={(e) => setPicked((s) => { const next = new Set(s); if (e.target.checked) next.add(r.id); else next.delete(r.id); return next; })} />
              <Link className="mail-row-link" to={link(r.id)}>
                <span className="mail-row-from">{r.unread && <span className="sr-only">Unread: </span>}{r.party_name ?? r.last_from ?? 'Unknown sender'}{r.messages > 1 && <span className="mail-row-count"> {r.messages}</span>}</span>
                <span className="mail-row-text"><span className="mail-row-subject">{r.subject}</span>{r.snippet && <span className="mail-row-snippet"> – {snippetText(r.snippet)}</span>}</span>
                <span className="mail-row-tags">
                  {r.attachments > 0 && <span className="mail-row-files" title={`${r.attachments} ${r.attachments === 1 ? 'file' : 'files'}`}><Icon name="attach" /><span className="sr-only">{r.attachments} {r.attachments === 1 ? 'file' : 'files'}</span></span>}
                  {r.status !== 'open' && <Badge tone={badgeTone(STATUS[r.status].tone)}>{STATUS[r.status].label}</Badge>}
                  {r.assignee_name && <Badge><span className="sr-only">Assigned to </span>{r.assignee_user_id === me ? 'You' : r.assignee_name}</Badge>}
                </span>
                <span className="mail-row-when"><time dateTime={r.last_message_at} title={new Date(r.last_message_at).toLocaleString()}>{mailWhen(r.last_message_at)}</time></span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function FolderLink({ to, label, count, unit, current }: { to: string; label: string; count: number; unit: string; current: boolean }) {
  return (
    <Link to={to} className={current ? 'on' : undefined} aria-current={current ? 'page' : undefined}>
      <span className="grow">{label}</span>{count > 0 && <span className="tree-count">{count}<span className="sr-only"> {unit}</span></span>}
    </Link>
  );
}

function ThreadView({ id, box, back, onBack, people, admin, me, onChange, onGone }: {
  id: string; box: Mailbox | null; back: string; onBack: () => void; people: Person[]; admin: boolean; me: string; onChange: () => void; onGone: () => void;
}) {
  const t = useApi<Thread>(`/v1/threads/${id}?html=true`);
  const images = useApi<ImageChoices>('/v1/mail/images');
  const toast = useToast();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [added, setAdded] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string> | null>(null);
  const [files, setFiles] = useState<Upload[]>([]);
  // Opening a conversation reads it, as in Gmail: once, when it first loads.
  const marked = useRef(false);
  useEffect(() => {
    if (!t.data || marked.current) return;
    marked.current = true;
    void api('/v1/threads/read', { body: { thread_ids: [t.data.id], read: true } }).then(onChange, () => undefined);
  }, [t.data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (t.error) return <><Button type="button" variant="ghost" size="sm" onClick={onBack}><Icon name="back" />Back to {back}</Button><ErrorBanner error={t.error} /></>;
  if (!t.data) return <ListSkeleton rows={3} label="Loading the conversation" />;
  const d = t.data;
  // The latest message is open; earlier ones fold to a line, as in Gmail.
  const expanded = open ?? new Set([d.messages[d.messages.length - 1]?.id ?? '']);
  const toggle = (mid: string) => setOpen(() => { const next = new Set(expanded); if (next.has(mid)) next.delete(mid); else next.add(mid); return next; });
  const partyId = d.party_id ?? added;
  // Answer the latest person who wrote; automated mail has nobody to answer.
  const lastIn = [...d.messages].reverse().find((m) => m.direction === 'in' && !m.automated);
  const automated = !lastIn && d.messages.find((m) => m.direction === 'in')?.automated;
  const to = lastIn?.from_address ?? d.messages.find((m) => m.direction === 'out')?.to_addresses[0] ?? '';
  // jordan.reyes@… reads as Jordan Reyes until the person types a better name.
  const guess = to.split('@')[0]!.split(/[._-]+/).filter(Boolean).map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
  const fullName = (name ?? guess).trim();

  const run = async (route: string, payload: Record<string, unknown>, done: string, gone = false) => {
    setBusy(true); setError(null);
    try {
      await api(route, { body: { thread_ids: [d.id], ...payload } });
      toast(done);
      if (gone) onGone(); else { await t.reload(); onChange(); }
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const alwaysImages = async (sender: string) => {
    setError(null);
    try { await api('/v1/mail/images', { body: { sender, show: true } }); await images.reload(); toast(`Images from ${sender} always show`); } catch (e) { setError(e); }
  };
  const addContact = async () => {
    setBusy(true); setError(null);
    try {
      if (!fullName) { setError(new Error('Give them a name.')); setBusy(false); return; }
      const [given, ...rest] = fullName.split(/\s+/);
      const out = await propose('party.create', { kind: 'person', given_name: given, ...(rest.length ? { family_name: rest.join(' ') } : {}), emails: [{ address: to }] });
      toast(outcomeMessage(out, 'Added as a contact'), outcomeTone(out));
      const pid = out.result?.party_id as string | undefined;
      if (pid) {
        // They wrote in, so a one-to-one answer is in scope (as for a known sender).
        await propose('consent.record', { party_id: pid, channel: 'email', address: to, action: 'opt_in', legal_basis: 'inbound_request', source: { kind: 'inbound_email' } });
        setAdded(pid);
        onChange();
      }
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const reply = async () => {
    if (!body.trim()) { setError(new Error('Write a reply first.')); return; }
    setBusy(true); setError(null);
    try {
      const out = await propose('comms.email_send', {
        party_id: partyId, to, subject: /^re:/i.test(d.subject) ? d.subject : `Re: ${d.subject}`, body_text: body.trim(), category: 'one_to_one',
        mailbox: box?.kind === 'user' && box.connected.length ? 'user' : 'workspace', thread_id: d.id, ...(lastIn ? { in_reply_to_message_id: lastIn.id } : {}),
        ...(files.length ? { attachment_ids: files.map((f) => f.id) } : {}),
      });
      toast(out.state === 'sent' ? 'Reply sent' : out.state === 'held' ? 'Reply queued: it goes out when quiet hours end' : outcomeMessage(out, 'Reply sent'), outcomeTone(out));
      if (out.state !== 'rejected') { setBody(''); setFiles([]); setOpen(null); await t.reload(); onChange(); }
    } catch (e) { setError(e); } finally { setBusy(false); }
  };

  return (
    <article className="stack inbox-item mail-thread" aria-labelledby="thread-h">
      <div className="mail-toolbar mail-thread-tools" role="group" aria-label="Conversation actions">
        <Button type="button" variant="ghost" size="sm" onClick={onBack}><Icon name="back" />Back to {back}</Button>
        {d.deleted_at ? (
          admin && <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void run('/v1/threads/restore', {}, 'Restored', true)}>Restore</Button>
        ) : (
          <>
            <StatusMenu current={d.status} onPick={(s) => void run('/v1/threads/triage', { status: s }, `Marked ${STATUS[s].label.toLowerCase()}`)} />
            <AssignMenu current={{ id: d.assignee_user_id, name: d.assignee_name }} people={people} me={me}
              onPick={(uid, who) => void run('/v1/threads/triage', { assignee_user_id: uid }, uid ? `Assigned to ${who}` : 'Unassigned')} />
            {admin && <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => void run('/v1/threads/delete', {}, 'Moved to Trash', true)}>Delete</Button>}
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void api('/v1/threads/read', { body: { thread_ids: [d.id], read: false } }).then(() => { toast('Marked unread'); onGone(); }, setError)}>Mark unread</Button>
          </>
        )}
      </div>
      <header className="stack" style={{ gap: 4 }}>
        <h2 id="thread-h" tabIndex={-1}>{d.subject}</h2>
        <div className="row mail-thread-meta">
          {box && <Badge>{boxName(box)}</Badge>}
          <Badge tone={badgeTone(STATUS[d.status].tone)}>{STATUS[d.status].label}</Badge>
          {d.assignee_user_id && <Badge>Assigned to {d.assignee_user_id === me ? 'you' : d.assignee_name}</Badge>}
          {d.deleted_at && <Badge tone="danger">In Trash since <Time iso={d.deleted_at} /></Badge>}
          {d.party_id && <span className="faint">With <PartyLink id={d.party_id} name={d.party_name} /></span>}
        </div>
      </header>
      {d.messages.length > 2 && (
        <div className="row"><Button type="button" variant="ghost" size="sm" onClick={() => setOpen(expanded.size === d.messages.length ? new Set() : new Set(d.messages.map((m) => m.id)))}>
          {expanded.size === d.messages.length ? 'Fold earlier messages' : `Expand all ${d.messages.length} messages`}
        </Button></div>
      )}
      <ol className="mail-messages">
        {d.messages.map((m) => {
          const on = expanded.has(m.id);
          return (
            <li key={m.id} className={`mail-message${m.direction === 'out' ? ' out' : ''}${on ? '' : ' folded'}`}>
              <button type="button" className="mail-message-head" aria-expanded={on} aria-controls={`msg-${m.id}`} onClick={() => toggle(m.id)}>
                <strong>{m.from_address}</strong>
                {m.automated && <Badge>{AUTOMATED[m.automated]}</Badge>}
                {!on && m.attachments.some((a) => !a.inline) && <span className="mail-row-files"><Icon name="attach" /><span className="sr-only">Has files</span></span>}
                {on ? (m.to_addresses.length > 0 && <span>to {m.to_addresses.join(', ')}</span>) : <span className="mail-message-snippet">{snippetText(m.body_text)}</span>}
                <span className="mail-when"><Time iso={m.at} full={on} /></span>
              </button>
              <div id={`msg-${m.id}`} hidden={!on}>
                {/* HTML waits for whose images show, so a trusted sender's mail loads once and never flashes the notice. */}
                {on && (!m.body_html ? <MailBody text={m.body_text} /> : !images.data && !images.error ? <ListSkeleton rows={2} label="Loading the message" /> : (
                  <HtmlBody html={m.body_html} from={m.from_address} message={m} shown={showsImages(images.data, m)}
                    onAlways={m.direction === 'in' && images.data ? () => void alwaysImages(m.from_address.toLowerCase()) : undefined} />
                ))}
                {on && <Files message={m} />}
              </div>
            </li>
          );
        })}
      </ol>
      <ErrorBanner error={error} />
      {d.deleted_at ? null : partyId && to ? (
        <div className="stack mail-reply" style={{ gap: 8 }}>
          <Field label={`Reply to ${to}`}><textarea className={textareaClass} rows={4} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
          <AttachFiles files={files} onChange={setFiles} onError={setError} />
          <div className="row"><Button type="submit" disabled={busy} onClick={() => void reply()}>{busy ? 'Sending…' : 'Send reply'}</Button></div>
        </div>
      ) : to ? (
        <div className="stack" style={{ gap: 6 }}>
          <p className="small muted" style={{ margin: 0 }}>{to} is not a contact yet. Add them to reply, so the conversation sits on their record.</p>
          <div className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label="Their name"><input className={fieldClass} value={name ?? guess} onChange={(e) => setName(e.target.value)} /></Field>
            <Button type="submit" variant="secondary" disabled={busy} onClick={() => void addContact()}>Add {to} as a contact</Button>
          </div>
        </div>
      ) : automated ? (
        <p className="small muted" style={{ margin: 0 }}>Sent automatically ({AUTOMATED[automated].toLowerCase()}), so there is nobody to answer.</p>
      ) : null}
    </article>
  );
}

// The files on a message, each a download. Pictures an HTML message shows in
// place are not listed again.
function Files({ message }: { message: Message }) {
  const list = message.attachments.filter((a) => !a.inline || !message.body_html);
  if (!list.length) return null;
  return (
    <ul className="mail-files" aria-label="Files">
      {list.map((a) => (
        <li key={a.id}>
          {a.kept ? (
            <a className="mail-file" href={fileUrl(message.id, a)} download={a.filename}><Icon name="attach" /><span className="mail-file-name">{a.filename}</span><span className="faint">{size(a.bytes)}</span></a>
          ) : (
            <span className="mail-file off"><Icon name="attach" /><span className="mail-file-name">{a.filename}</span><span className="faint">{size(a.bytes)}, too large to keep</span></span>
          )}
        </li>
      ))}
    </ul>
  );
}

// Files to send: each uploads as it is picked, and goes out with the message.
function AttachFiles({ files, onChange, onError }: { files: Upload[]; onChange: (f: Upload[]) => void; onError: (e: unknown) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const add = async (list: FileList | null) => {
    if (!list?.length) return;
    setBusy(true); onError(null);
    try {
      const form = new FormData();
      for (const f of Array.from(list)) form.append('file', f, f.name);
      onChange([...files, ...(await api<Upload[]>('/v1/mail/attachments', { form }))]);
    } catch (e) { onError(e); } finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  return (
    <div className="mail-attach">
      <input ref={input} type="file" multiple className="sr-only" tabIndex={-1} aria-hidden="true" data-testid="attach-input" onChange={(e) => void add(e.target.files)} />
      <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => input.current?.click()}><Icon name="attach" />{busy ? 'Uploading…' : 'Attach files'}</Button>
      {files.length > 0 && (
        <ul className="mail-files" aria-label="Files to send">
          {files.map((f) => (
            <li key={f.id}><span className="mail-file"><Icon name="attach" /><span className="mail-file-name">{f.filename}</span><span className="faint">{size(f.bytes)}</span>
              <Button type="button" variant="ghost" size="sm" className="mail-file-remove" aria-label={`Remove ${f.filename}`} onClick={() => onChange(files.filter((x) => x.id !== f.id))}><Icon name="x" /></Button></span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

// A new message, as in Gmail. It goes to a contact, so an address that is not
// one becomes one; and a contact with no email consent gets one only when the
// writer confirms a business reason (recorded as legitimate interest, under
// their name). Someone who opted out cannot be written to here.
interface PartyHit { id: string; display_name: string; email: string | null }
const BLOCKING = ['opt_out', 'unsubscribe', 'bounce_suppress', 'complaint_suppress'];
function Compose({ boxes, from, onClose, onSent }: { boxes: Mailbox[]; from: Mailbox | null; onClose: () => void; onSent: (boxId: string) => void }) {
  const toast = useToast();
  const team = boxes.find((b) => b.kind === 'team');
  const mine = boxes.find((b) => b.mine && b.connected.length > 0);
  const senders = [
    ...(mine ? [{ key: 'user' as const, box: mine, label: `You, as ${mine.connected[0]!.email}` }] : []),
    ...(team ? [{ key: 'workspace' as const, box: team, label: `${team.name}${team.address ? ` (${team.address})` : ''}` }] : []),
  ];
  const [sender, setSender] = useState<'user' | 'workspace'>(from?.mine && mine ? 'user' : senders[0]?.key ?? 'workspace');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<Upload[]>([]);
  const [name, setName] = useState<string | null>(null);
  const [reason, setReason] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const address = to.trim().toLowerCase();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address);
  const typed = useDebounced(address, 300);
  const hits = useApi<{ items: PartyHit[] }>(typed.length >= 2 ? `/v1/parties?q=${encodeURIComponent(typed)}&limit=6` : null);
  const settled = typed === address && !hits.loading;
  const contact = valid && settled ? hits.data?.items.find((p) => p.email?.toLowerCase() === address) ?? null : null;
  const consent = useApi<{ email: { may_send: boolean; latest: { action: string } | null } }>(contact ? `/v1/parties/${contact.id}/consent` : null);
  const consentNow = contact && !consent.loading ? consent.data : null;
  const blocked = !!consentNow && BLOCKING.includes(consentNow.email.latest?.action ?? '');
  const newContact = valid && settled && !contact;
  const needsReason = !blocked && (newContact || (!!consentNow && !consentNow.email.may_send));
  const guess = address.split('@')[0]!.split(/[._-]+/).filter(Boolean).map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
  const via = senders.find((x) => x.key === sender);

  const send = async () => {
    setError(null);
    if (!valid) return setError(new Error('Give an email address to send to.'));
    if (!subject.trim()) return setError(new Error('Give the message a subject.'));
    if (!body.trim()) return setError(new Error('Write the message first.'));
    if (blocked) return setError(new Error(`${contact?.display_name ?? address} opted out of email, so Mazu will not write to them.`));
    if (needsReason && !reason) return setError(new Error('Confirm you have a business reason to write to them.'));
    setBusy(true);
    try {
      let partyId = contact?.id ?? null;
      if (!partyId) {
        const full = (name ?? guess).trim();
        if (!full) { setBusy(false); return setError(new Error('Give them a name.')); }
        const [given, ...rest] = full.split(/\s+/);
        const made = await propose('party.create', { kind: 'person', given_name: given, ...(rest.length ? { family_name: rest.join(' ') } : {}), emails: [{ address }] });
        partyId = (made.result?.party_id as string | undefined) ?? null;
        if (!partyId) { toast(outcomeMessage(made, 'Added as a contact'), outcomeTone(made)); setBusy(false); return; }
      }
      if (needsReason) {
        const c = await propose('consent.record', { party_id: partyId, channel: 'email', address, action: 'opt_in', legal_basis: 'legitimate_interest', source: { kind: 'person', detail: 'Writing to them from Mail' } });
        if (c.state === 'rejected') { toast(outcomeMessage(c), 'error'); setBusy(false); return; }
      }
      const out = await propose('comms.email_send', {
        party_id: partyId, to: address, subject: subject.trim(), body_text: body.trim(), category: 'one_to_one', mailbox: sender,
        ...(files.length ? { attachment_ids: files.map((f) => f.id) } : {}),
      });
      toast(out.state === 'sent' ? 'Message sent' : out.state === 'held' ? 'Message queued: it goes out when quiet hours end' : outcomeMessage(out, 'Message sent'), outcomeTone(out));
      if (out.state !== 'rejected') onSent(via?.box.id ?? team?.id ?? '');
    } catch (e) { setError(e); } finally { setBusy(false); }
  };

  // A message with anything in it is asked about before it is thrown away.
  const [discarding, setDiscarding] = useState(false);
  const discard = () => (to.trim() || subject.trim() || body.trim() || files.length ? setDiscarding(true) : onClose());

  return (
    <article className="stack mail-thread mail-compose-form" aria-labelledby="compose-h">
      <div className="mail-toolbar mail-thread-tools" role="group" aria-label="Message actions">
        <Button type="button" variant="ghost" size="sm" onClick={discard}><Icon name="back" />Discard</Button>
      </div>
      <h2 id="compose-h">New message</h2>
      {senders.length > 1 ? (
        <Field label="From">
          <select className={selectClass} value={sender} onChange={(e) => setSender(e.target.value as 'user' | 'workspace')}>
            {senders.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
          </select>
        </Field>
      ) : via && <p className="small muted" style={{ margin: 0 }}>From {via.label}</p>}
      <Field label="To">
        <input className={fieldClass} type="email" list="compose-contacts" autoComplete="off" value={to} onChange={(e) => { setTo(e.target.value); setReason(false); }} />
      </Field>
      <datalist id="compose-contacts">
        {(hits.data?.items ?? []).filter((p) => p.email).map((p) => <option key={p.id} value={p.email!}>{p.display_name}</option>)}
      </datalist>
      {contact && <p className="small muted" style={{ margin: 0 }}>To <PartyLink id={contact.id} name={contact.display_name} />.</p>}
      {blocked && <Alert tone="danger" role="alert">{contact?.display_name} opted out of email, so Mazu will not write to them.</Alert>}
      {newContact && (
        <div className="stack" style={{ gap: 6 }}>
          <p className="small muted" style={{ margin: 0 }}>{address} is not a contact yet. Sending adds them, so the conversation sits on their record.</p>
          <Field label="Their name"><input className={fieldClass} value={name ?? guess} onChange={(e) => setName(e.target.value)} /></Field>
        </div>
      )}
      {needsReason && (
        <label className="small mail-consent">
          <input type="checkbox" className="mail-check" checked={reason} onChange={(e) => setReason(e.target.checked)} />
          <span>I have a business reason to email them. Mazu records it as their consent to one-to-one email (legitimate interest), under your name.</span>
        </label>
      )}
      <Field label="Subject"><input className={fieldClass} value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={300} /></Field>
      <Field label="Message"><textarea className={textareaClass} rows={10} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
      <AttachFiles files={files} onChange={setFiles} onError={setError} />
      <ErrorBanner error={error} />
      <div className="row" style={{ gap: 8 }}>
        <Button type="button" disabled={busy} onClick={() => void send()}>{busy ? 'Sending…' : 'Send'}</Button>
        <Button type="button" variant="ghost" onClick={discard}>Discard</Button>
      </div>
      <ConfirmDialog open={discarding} title="Discard this message?" confirmLabel="Discard message" tone="danger" onConfirm={onClose} onCancel={() => setDiscarding(false)}>
        <p style={{ margin: 0 }}>What you wrote is not saved anywhere.</p>
      </ConfirmDialog>
    </article>
  );
}

// The reader always shows images from everyone, or from this message's sender.
const showsImages = (c: ImageChoices | null, m: Message) => !!c && (c.everyone || (m.direction === 'in' && c.senders.includes(m.from_address.toLowerCase())));

// HTML mail in a frame that runs no script (no allow-scripts, and a CSP that
// says so), submits no form, and opens links in a new tab. Remote images and
// fonts stay blocked, so a tracking pixel learns nothing, until the reader asks
// for them, here or always (`shown`). Same origin without script lets the page
// size the frame to fit.
const REMOTE = /<img[^>]+src\s*=\s*["']?https?:|url\(\s*["']?https?:|<link[^>]+href\s*=\s*["']?https?:/i;
function HtmlBody({ html: raw, from, message, shown, onAlways }: { html: string; from: string; message: Message; shown: boolean; onAlways?: () => void }) {
  const [asked, setAsked] = useState(false);
  const images = asked || shown;
  const [height, setHeight] = useState(160);
  const frame = useRef<HTMLIFrameElement>(null);
  const observer = useRef<ResizeObserver | null>(null);
  useEffect(() => () => observer.current?.disconnect(), []);
  // Showing images reloads the frame, so its new body is watched afresh.
  useEffect(() => { observer.current?.disconnect(); observer.current = null; }, [images]);
  // A picture the message carries (cid:) is read from Mazu, so it shows without asking.
  const byCid = new Map(message.attachments.filter((a) => a.content_id && a.kept).map((a) => [a.content_id!.toLowerCase(), a]));
  const html = raw.replace(/(["'(\s=])cid:([^"')\s>]+)/gi, (all, lead: string, cid: string) => {
    const a = byCid.get(decodeURIComponent(cid).toLowerCase());
    return a ? `${lead}${window.location.origin}${fileUrl(message.id, a, true)}` : all;
  });
  const own = window.location.origin;
  const remote = REMOTE.test(html.replaceAll(own, ''));
  const csp = `default-src 'none'; style-src 'unsafe-inline'${images ? ' https:' : ''}; img-src data: ${own}${images ? ' https:' : ''}; font-src${images ? ' https:' : " 'none'"}`;
  const doc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><base target="_blank">`
    + '<style>html,body{margin:0;padding:0;background:#fff;color:#1a1f2b}body{font:14px/1.5 system-ui,-apple-system,sans-serif;overflow-wrap:anywhere;overflow-y:hidden}img{max-width:100%;height:auto}</style>'
    + `</head><body>${html}</body></html>`;
  const fit = () => {
    const b = frame.current?.contentDocument?.body;
    if (!b) return;
    setHeight(Math.min(Math.max(b.scrollHeight, b.offsetHeight) + 4, 30_000));
    if (!observer.current && 'ResizeObserver' in window) { observer.current = new ResizeObserver(fit); observer.current.observe(b); }
  };
  return (
    <div className="mail-html">
      {remote && !images && (
        <p className="small muted mail-images">Images from the web are hidden, so the sender cannot tell you opened this.
          {' '}<Button type="button" variant="ghost" size="sm" onClick={() => setAsked(true)}>Show images</Button>
          {onAlways && <Button type="button" variant="ghost" size="sm" onClick={onAlways}>Always show images from {from}</Button>}</p>
      )}
      <iframe ref={frame} title={`Message from ${from}`} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={doc} onLoad={fit} style={{ height }} />
    </div>
  );
}

// Paragraphs with each web address as a link named by its host (the full
// address is its title), and the quoted earlier message folded away.
function MailBody({ text }: { text: string }) {
  const { body, quoted } = mailText(text);
  const paras = (ps: Span[][]) => ps.map((p, i) => <p key={i}>{p.map((s, j) => <Fragment key={j}>{'text' in s ? s.text : <a href={s.href} title={s.href} target="_blank" rel="noopener noreferrer nofollow">{s.host}</a>}</Fragment>)}</p>);
  return (
    <div className="mail-body">
      {body.length ? paras(body) : !quoted.length && <p className="muted">No text in this message.</p>}
      {quoted.length > 0 && <Expandable label={['Show quoted text', 'Hide quoted text']}><div className="mail-quoted">{paras(quoted)}</div></Expandable>}
    </div>
  );
}

// Settings: connect Gmail or Microsoft 365, sync now, whose images show, and who
// sees your calendar.
export function MailAndCalendarSettings() {
  const ids = useApi<Array<{ id: string; provider: string; email: string; status: string; created_at: string }>>('/v1/oauth-identities');
  const cals = useApi<Array<{ id: string; mine: boolean }>>('/v1/calendars');
  const toast = useToast();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const connected = params.get('connected');
  const failed = params.get('connect_error');
  const connect = async (provider: 'google' | 'microsoft') => {
    setError(null); setBusy(true);
    try { window.location.assign((await api<{ url: string }>(`/v1/oauth-identities/${provider}/connect`, { method: 'POST' })).url); } catch (e) { setError(e); setBusy(false); }
  };
  const [disconnecting, setDisconnecting] = useState<{ id: string; email: string } | null>(null);
  const disconnect = async () => {
    if (!disconnecting) return;
    setError(null); setBusy(true);
    try { await api(`/v1/oauth-identities/${disconnecting.id}/disconnect`, { method: 'POST' }); toast('Disconnected'); await ids.reload(); } catch (e) { setError(e); }
    finally { setBusy(false); setDisconnecting(null); }
  };
  const sync = async () => {
    setError(null); setBusy(true);
    try { const r = await api<{ synced: number }>('/v1/mailboxes/sync', { method: 'POST' }); toast(`Synced ${r.synced} new ${r.synced === 1 ? 'item' : 'items'}`); } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const active = ids.data?.filter((i) => i.status === 'active') ?? [];
  const own = cals.data?.find((c) => c.mine);
  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="mail-h">
      <h2 id="mail-h">Mail and calendar</h2>
      <p className="small muted" style={{ margin: 0 }}>Connect your work Gmail or Microsoft 365 account. Its mail lands in your Mazu mailbox and its events on your Mazu calendar, and replies you send from Mazu go out as you.</p>
      {connected && <Alert tone="success" role="status">Connected {connected === 'google' ? 'Google' : 'Microsoft'}.</Alert>}
      {failed && <Alert tone="danger" role="alert">Could not connect: {failed.replace(/_/g, ' ')}.</Alert>}
      <ErrorBanner error={error} />
      {active.length > 0 && (
        <ul className="list" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {active.map((i) => (
            <li key={i.id} className="list-item">
              <div className="grow"><div className="item-title">{i.email}</div><div className="item-sub">{i.provider === 'google' ? 'Gmail and Google Calendar' : 'Microsoft 365'} · connected <Time iso={i.created_at} /></div></div>
              <Button type="submit" variant="secondary" size="sm" onClick={() => setDisconnecting(i)}>Disconnect<span className="sr-only"> {i.email}</span></Button>
            </li>
          ))}
        </ul>
      )}
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Button type="submit" disabled={busy} onClick={() => void connect('google')}>Connect Gmail</Button>
        <Button type="submit" variant="secondary" disabled={busy} onClick={() => void connect('microsoft')}>Connect Microsoft 365</Button>
        {active.length > 0 && <Button type="submit" variant="ghost" disabled={busy} onClick={() => void sync()}>Sync now</Button>}
      </div>
      <MailImages />
      {own && <CalendarSharing calendarId={own.id} />}
      <ConfirmDialog open={!!disconnecting} title={`Disconnect ${disconnecting?.email ?? ''}?`} confirmLabel="Disconnect" tone="danger" busy={busy} onConfirm={() => void disconnect()} onCancel={() => setDisconnecting(null)}>
        <p style={{ margin: 0 }}>Nothing syncs or sends through this account any more. Mail and events that already arrived stay. You can connect it again later.</p>
      </ConfirmDialog>
    </section>
  );
}

// Images from the web: held back by default, shown always from everyone or from
// the senders picked in an open message (Always show images from …).
function MailImages() {
  const c = useApi<ImageChoices>('/v1/mail/images');
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const save = async (sender: string, show: boolean, done: string) => {
    setError(null);
    try { await api('/v1/mail/images', { body: { sender, show } }); await c.reload(); toast(done); } catch (e) { setError(e); }
  };
  return (
    <div className="stack" style={{ gap: 8 }}>
      <h3 style={{ margin: 0, fontSize: '1rem' }}>Images in mail</h3>
      <p className="small muted" style={{ margin: 0 }}>Mail hides images from the web until you ask, because loading one can tell the sender you opened their message, and roughly where you are.</p>
      <ErrorBanner error={error ?? c.error} />
      {c.data && (
        <>
          <label className="small mail-consent">
            <input type="checkbox" className="mail-check" checked={c.data.everyone}
              onChange={(e) => void save('*', e.target.checked, e.target.checked ? 'Images always show' : 'Images are hidden until you ask')} />
            <span>Always show images from the web</span>
          </label>
          {c.data.senders.length > 0 && (
            <ul className="list" aria-label="Senders whose images always show" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {c.data.senders.map((x) => (
                <li key={x} className="list-item">
                  <div className="grow">{x}</div>
                  <Button type="submit" variant="secondary" size="sm" aria-label={`Remove ${x}`} onClick={() => void save(x, false, `Images from ${x} are hidden until you ask`)}>Remove</Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function CalendarSharing({ calendarId }: { calendarId: string }) {
  const shares = useApi<Array<{ id: string; grantee_kind: string; grantee_id: string | null; grantee_name: string; level: string }>>(`/v1/calendars/${calendarId}/shares`);
  const users = useApi<Array<{ id: string; display_name: string; status: string }>>('/v1/users');
  const [who, setWho] = useState('workspace');
  const [level, setLevel] = useState<'details' | 'busy' | 'none'>('details');
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const save = async (body: Record<string, unknown>) => {
    setError(null);
    try { await api(`/v1/calendars/${calendarId}/shares`, { body }); toast('Sharing updated'); await shares.reload(); } catch (e) { setError(e); }
  };
  const grantee = (w: string) => (w === 'workspace' ? { grantee_kind: 'workspace' } : { grantee_kind: 'user', grantee_user_id: w });
  return (
    <div className="stack" style={{ gap: 8 }}>
      <h3 style={{ margin: 0, fontSize: '1rem' }}>Who sees your calendar</h3>
      <p className="small muted" style={{ margin: 0 }}>By default your teammates see when you are busy and your manager sees the details. Share more, or hide it from someone.</p>
      <ErrorBanner error={error} />
      {shares.data && shares.data.length > 0 && (
        <ul className="list" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {shares.data.map((s) => (
            <li key={s.id} className="list-item">
              <div className="grow">{s.grantee_name} · {s.level === 'details' ? 'sees details' : s.level === 'busy' ? 'sees busy times' : 'sees nothing'}</div>
              <Button type="submit" variant="secondary" size="sm" onClick={() => void save({ ...grantee(s.grantee_kind === 'workspace' ? 'workspace' : s.grantee_id!), level: 'default' })}>Remove</Button>
            </li>
          ))}
        </ul>
      )}
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Share with">
          <select className={selectClass} value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="workspace">Everyone in the workspace</option>
            {users.data?.filter((u) => u.status === 'active').map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
          </select>
        </Field>
        <Field label="They see">
          <select className={selectClass} value={level} onChange={(e) => setLevel(e.target.value as typeof level)}>
            <option value="details">Details</option><option value="busy">Busy times only</option><option value="none">Nothing</option>
          </select>
        </Field>
        <Button type="submit" variant="secondary" onClick={() => void save({ ...grantee(who), level })}>Save</Button>
      </div>
    </div>
  );
}
